import { consent } from './consent';

/**
 * Storage for settings the visitor makes (language, sound, quality): the
 * "preferences" consent category. The value always lives in memory
 * for this page load, so a choice works at once and carries from the landing
 * into the game; it reaches localStorage only once preferences are allowed.
 * Granting preferences later writes what is in memory; rejecting them removes
 * what was stored.
 *
 * Every key here is listed in `cookie-table.ts` under `preferences`.
 */
export const PREF_KEYS = [
  'admiralty.settings.v1',
  'admiralty.locale',
  'landing.sound',
  'admiralty.tree.howto.hidden',
] as const;
export type PrefKey = (typeof PREF_KEYS)[number];

const memory = new Map<PrefKey, string>();

function persist(key: PrefKey, raw: string): void {
  try {
    localStorage.setItem(key, raw);
  } catch {
    /* storage blocked: memory still holds it for this visit */
  }
}

export const prefStorage = {
  get(key: PrefKey): string | null {
    const m = memory.get(key);
    if (m !== undefined) return m;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: PrefKey, raw: string): void {
    memory.set(key, raw);
    if (consent.allowed('preferences')) persist(key, raw);
  },
  /** Tests. */
  _clear(): void {
    memory.clear();
  },
};

consent.onChange((rec) => {
  if (rec.choices.preferences) {
    for (const [k, v] of memory) persist(k, v);
    return;
  }
  for (const k of PREF_KEYS) {
    try {
      // Keep what this visit chose in memory, drop it from the disk.
      const v = localStorage.getItem(k);
      if (v !== null && !memory.has(k)) memory.set(k, v);
      localStorage.removeItem(k);
    } catch {
      /* storage blocked */
    }
  }
});
